import { useEffect, useMemo, useState } from "react";
import { UserCog, Plus, Pencil, Trash2, KeyRound, ShieldCheck, Users, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PageHeader from "@/components/PageHeader";
import SearchBox from "@/components/SearchBox";
import EmptyState from "@/components/EmptyState";
import PhotoUploader from "@/components/PhotoUploader";
import { maskCPF, maskRG, maskPhone, maskCEP, onlyDigits, fetchCEP } from "@/lib/masks";
import { useCrudList, crudCreate, crudUpdate, crudDelete } from "@/lib/crud";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export const CARGOS = [
  { value: "terapeuta", label: "Terapeuta", tone: "bg-[#EDE1F3] text-[#7B569A]" },
  { value: "coordenador", label: "Coordenador", tone: "bg-[#DFF4F5] text-[#2A7A80]" },
  { value: "recepcionista", label: "Recepcionista", tone: "bg-[#FBE1EC] text-[#C4547E]" },
  { value: "financeiro", label: "Financeiro", tone: "bg-[#FFF3D6] text-[#B18319]" },
  { value: "condutor", label: "Condutor", tone: "bg-emerald-100 text-emerald-700" },
  { value: "lateral", label: "Lateral", tone: "bg-blue-100 text-blue-700" },
  { value: "tratador", label: "Tratador", tone: "bg-amber-100 text-amber-800" },
  { value: "limpeza", label: "Limpeza", tone: "bg-slate-100 text-slate-700" },
  { value: "estagiario", label: "Estagiário", tone: "bg-cyan-100 text-cyan-700" },
  { value: "voluntario", label: "Voluntário", tone: "bg-rose-100 text-rose-700" },
];

export const ESPECIALIDADES = [
  { value: "psicologo", label: "Psicólogo(a)" },
  { value: "fisioterapeuta", label: "Fisioterapeuta" },
  { value: "terapeuta_ocupacional", label: "Terapeuta Ocupacional" },
  { value: "fonoaudiologo", label: "Fonoaudiólogo(a)" },
  { value: "pedagogo", label: "Pedagogo(a)" },
  { value: "educador_fisico", label: "Educador(a) Físico" },
];

const SITUACOES = [
  { value: "ativo", label: "Ativo", tone: "bg-emerald-100 text-emerald-700" },
  { value: "afastado", label: "Afastado", tone: "bg-amber-100 text-amber-800" },
  { value: "ferias", label: "Férias", tone: "bg-cyan-100 text-cyan-700" },
  { value: "inativo", label: "Inativo", tone: "bg-slate-100 text-slate-700" },
];

const TIPOS_CONTRATO = [
  { value: "clt", label: "CLT" },
  { value: "pj", label: "PJ" },
  { value: "autonomo", label: "Autônomo" },
  { value: "voluntario", label: "Voluntário" },
  { value: "estagio", label: "Estágio" },
];

const CARGOS_COM_LOGIN = ["terapeuta", "coordenador", "recepcionista", "financeiro"];

const emptyForm = {
  nome: "", cargo: "terapeuta", especialidade: "",
  data_nasc: "", sexo: "", cpf: "", rg: "",
  telefone: "", email: "",
  endereco: { logradouro: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "", cep: "" },
  registro_conselho: "", formacao: "", data_admissao: "",
  situacao: "ativo", tipo_contrato: "",
  salario_mensal: "", valor_hora: "", dia_pagamento: "",
  pagamentos: [],
  dados_bancarios: { pix_tipo: "", pix_chave: "", banco: "", agencia: "", conta: "", tipo_conta: "" },
  disponibilidade: "", ativo: true, observacoes: "",
  criar_login: false, senha_temporaria: "",
};

const brl = (v) => v != null
  ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v)
  : "—";

function cargoMeta(v) { return CARGOS.find((c) => c.value === v) || { label: v, tone: "bg-slate-100 text-slate-700" }; }
function situacaoMeta(v) { return SITUACOES.find((s) => s.value === v) || SITUACOES[0]; }
function especialidadeLabel(v) { return ESPECIALIDADES.find((e) => e.value === v)?.label || ""; }

export default function Funcionarios() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const canSeeFinanceiro = user.role === "admin" || user.role === "financeiro";
  const canWrite = canSeeFinanceiro;
  const canDelete = isAdmin;

  const [q, setQ] = useState("");
  const [cargoFilter, setCargoFilter] = useState("todos");
  const params = { q };
  if (cargoFilter !== "todos") params.cargo = cargoFilter;

  const { items, loading, setItems, refetch } = useCrudList("/funcionarios", params);
  const [kpis, setKpis] = useState({});

  const fetchKpis = async () => {
    try {
      const { data } = await api.get("/funcionarios/kpis");
      setKpis(data);
    } catch (err) {
      console.error("Falha ao carregar KPIs de funcionários", err);
    }
  };
  useEffect(() => { fetchKpis(); }, [items.length]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [credentials, setCredentials] = useState(null); // {email, senha_temporaria}
  const [credentialsOpen, setCredentialsOpen] = useState(false);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm }); setOpen(true); };
  const openEdit = (f) => {
    setEditing(f);
    setForm({
      ...emptyForm, ...f,
      salario_mensal: f.salario_mensal ?? "",
      valor_hora: f.valor_hora ?? "",
      dia_pagamento: f.dia_pagamento ?? "",
      pagamentos: Array.isArray(f.pagamentos) ? f.pagamentos.map((p) => ({ _k: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `pg-${Date.now()}-${Math.random()}`, dia: p.dia ?? "", valor: p.valor ?? "", descricao: p.descricao || "" })) : [],
      endereco: { ...emptyForm.endereco, ...(f.endereco || {}) },
      dados_bancarios: { ...emptyForm.dados_bancarios, ...(f.dados_bancarios || {}) },
      criar_login: false, senha_temporaria: "",
    });
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // clean numeric fields
      payload.salario_mensal = payload.salario_mensal === "" ? null : parseFloat(payload.salario_mensal);
      payload.valor_hora = payload.valor_hora === "" ? null : parseFloat(payload.valor_hora);
      payload.dia_pagamento = payload.dia_pagamento === "" ? null : parseInt(payload.dia_pagamento, 10);
      // clean pagamentos array: valida dia + valor
      payload.pagamentos = (payload.pagamentos || [])
        .map((p) => ({
          dia: p.dia === "" || p.dia == null ? null : parseInt(p.dia, 10),
          valor: p.valor === "" || p.valor == null ? null : parseFloat(p.valor),
          descricao: p.descricao || "",
        }))
        .filter((p) => p.dia && p.valor && p.dia >= 1 && p.dia <= 31 && p.valor > 0);
      if (payload.cargo !== "terapeuta") payload.especialidade = null;
      if (editing) {
        delete payload.criar_login;
        delete payload.senha_temporaria;
        const upd = await crudUpdate("/funcionarios", editing.id, payload);
        setItems((prev) => prev.map((x) => x.id === upd.id ? upd : x));
        toast.success("Funcionário atualizado");
        setOpen(false);
      } else {
        // for CREATE, mock crudCreate response to catch login_criado
        const { data: created } = await api.post("/funcionarios", payload);
        setItems((prev) => [created, ...prev]);
        if (created.login_criado) {
          setCredentials(created.login_criado);
        } else {
          toast.success("Funcionário cadastrado");
        }
        setEditing(created);
        // Keep dialog open only if login was created (to show credentials) — otherwise close
        if (!created.login_criado) setOpen(false);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = "Erro ao salvar";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((e) => `${e.loc?.slice(-1)[0] || ""}: ${e.msg}`).join(" · ");
      else if (detail?.msg) msg = detail.msg;
      else if (err.message) msg = err.message;
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await crudDelete("/funcionarios", deleteId);
      setItems((prev) => prev.filter((x) => x.id !== deleteId));
      toast.success("Funcionário excluído (movido para Lixeira)");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setDeleteId(null); }
  };

  const handleCreateLogin = async (f) => {
    try {
      const { data } = await api.post(`/funcionarios/${f.id}/create-login`, {});
      setCredentials(data);
      setCredentialsOpen(true);
      setItems((prev) => prev.map((x) => x.id === f.id ? { ...x, user_id: "linked" } : x));
      toast.success("Login criado");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const handleResetPassword = async (f) => {
    if (!window.confirm(`Gerar nova senha temporária para ${f.nome}?`)) return;
    try {
      const { data } = await api.post(`/funcionarios/${f.id}/reset-password`);
      setCredentials({ email: f.email, senha_temporaria: data.senha_temporaria });
      setCredentialsOpen(true);
      toast.success("Senha resetada");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const filtered = useMemo(() => items, [items]);

  return (
    <div data-testid="funcionarios-page">
      <PageHeader
        icon={UserCog}
        title="Funcionários"
        description="Equipe do centro — terapeutas, coordenação, condutor, lateral, tratador e demais colaboradores."
        actions={canWrite && <Button onClick={openNew} data-testid="funcionario-new-btn" className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white"><Plus className="h-4 w-4 mr-1" />Novo funcionário</Button>}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatBox label="Total ativos" value={kpis.por_situacao?.ativo || 0} tone="turquoise" icon={Users} />
        <StatBox label="Terapeutas" value={kpis.por_cargo?.terapeuta || 0} tone="lilac" />
        <StatBox label="Perfis a completar" value={kpis.perfis_incompletos || 0} tone="pink" />
        {canSeeFinanceiro && <StatBox label="Folha mensal" value={brl(kpis.folha_mensal)} tone="gold" />}
      </div>

      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <SearchBox value={q} onChange={setQ} placeholder="Buscar por nome, cargo, especialidade…" testId="funcionarios-search" />
        <Tabs value={cargoFilter} onValueChange={setCargoFilter}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="todos" data-testid="cargo-tab-todos">Todos</TabsTrigger>
            {CARGOS.map((c) => (
              <TabsTrigger key={c.value} value={c.value} data-testid={`cargo-tab-${c.value}`}>{c.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <Card className="border-border shadow-none rounded-xl bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground">Carregando…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6"><EmptyState icon={UserCog} title="Nenhum funcionário" description="Cadastre a equipe do centro." action={canWrite && <Button onClick={openNew} className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white"><Plus className="h-4 w-4 mr-1" />Novo funcionário</Button>} /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Especialidade / Registro</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead>Acesso</TableHead>
                {canSeeFinanceiro && <TableHead className="text-right">Salário</TableHead>}
                <TableHead className="w-40"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((f) => {
                  const cm = cargoMeta(f.cargo);
                  const sm = situacaoMeta(f.situacao);
                  const hasLogin = !!f.user_id;
                  const canHaveLogin = CARGOS_COM_LOGIN.includes(f.cargo);
                  return (
                    <TableRow key={f.id} data-testid={`funcionario-row-${f.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          {f.foto_url ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${f.foto_url}`} alt="" className="h-9 w-9 rounded-full object-cover border border-border" />
                          ) : (
                            <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-brand-turquoise">
                              {(f.nome || "?").slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="text-sm">{f.nome}</div>
                            {f.email && <div className="text-[11px] text-muted-foreground">{f.email}</div>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><Badge className={`${cm.tone} border-0`}>{cm.label}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {f.cargo === "terapeuta" && especialidadeLabel(f.especialidade)}
                        {f.registro_conselho && <div className="text-[11px] text-muted-foreground">{f.registro_conselho}</div>}
                      </TableCell>
                      <TableCell><Badge className={`${sm.tone} border-0`}>{sm.label}</Badge></TableCell>
                      <TableCell>
                        {hasLogin ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0 gap-1"><ShieldCheck className="h-3 w-3" />Ativo</Badge>
                        ) : canHaveLogin && isAdmin ? (
                          <Button size="sm" variant="outline" onClick={() => handleCreateLogin(f)} data-testid={`funcionario-create-login-${f.id}`}>
                            <KeyRound className="h-3 w-3 mr-1" /> Criar login
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      {canSeeFinanceiro && <TableCell className="text-right font-medium">{brl(f.salario_mensal)}</TableCell>}
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          {canWrite && <Button size="icon" variant="ghost" onClick={() => openEdit(f)} data-testid={`funcionario-edit-${f.id}`}><Pencil className="h-4 w-4" /></Button>}
                          {hasLogin && isAdmin && (
                            <Button size="icon" variant="ghost" onClick={() => handleResetPassword(f)} title="Resetar senha" data-testid={`funcionario-reset-${f.id}`}><RefreshCw className="h-4 w-4 text-amber-600" /></Button>
                          )}
                          {canDelete && <Button size="icon" variant="ghost" onClick={() => setDeleteId(f.id)} data-testid={`funcionario-delete-${f.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCredentials(null); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar funcionário" : "Novo funcionário"}</DialogTitle>
            <DialogDescription>Cadastro completo com dados profissionais, financeiros e (opcionalmente) acesso ao sistema.</DialogDescription>
          </DialogHeader>

          {credentials && (
            <div className="rounded-lg border-2 border-brand-gold bg-[#FFF9E6] p-4 space-y-2">
              <p className="text-sm font-semibold text-[#8B6914]">🔑 Login criado — copie e envie estas credenciais ao funcionário:</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">E-mail:</span>
                  <code className="bg-white px-2 py-1 rounded border">{credentials.email}</code>
                  <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard.writeText(credentials.email); toast.success("E-mail copiado"); }}><Copy className="h-3 w-3" /></Button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">Senha:</span>
                  <code className="bg-white px-2 py-1 rounded border font-mono">{credentials.senha_temporaria}</code>
                  <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard.writeText(credentials.senha_temporaria); toast.success("Senha copiada"); }}><Copy className="h-3 w-3" /></Button>
                </div>
              </div>
              <p className="text-xs text-[#8B6914]">No primeiro acesso, o funcionário deve completar o cadastro e definir uma nova senha.</p>
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-6">
            {editing && (
              <PhotoUploader entity="funcionarios" entityId={editing.id} currentUrl={editing.foto_url}
                onUploaded={(url) => {
                  setEditing({ ...editing, foto_url: url });
                  setItems((prev) => prev.map((x) => x.id === editing.id ? { ...x, foto_url: url } : x));
                }} />
            )}

            <Section title="Cargo e função">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <F label="Cargo" req>
                  <Select value={form.cargo} onValueChange={(v) => setForm({ ...form, cargo: v, especialidade: v === "terapeuta" ? form.especialidade : "" })}>
                    <SelectTrigger data-testid="funcionario-form-cargo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CARGOS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                {form.cargo === "terapeuta" && (
                  <F label="Especialidade" req>
                    <Select value={form.especialidade} onValueChange={(v) => setForm({ ...form, especialidade: v })}>
                      <SelectTrigger data-testid="funcionario-form-especialidade"><SelectValue placeholder="Selecione…" /></SelectTrigger>
                      <SelectContent>
                        {ESPECIALIDADES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </F>
                )}
                <F label="Situação">
                  <Select value={form.situacao} onValueChange={(v) => setForm({ ...form, situacao: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{SITUACOES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Tipo de contrato">
                  <Select value={form.tipo_contrato || ""} onValueChange={(v) => setForm({ ...form, tipo_contrato: v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{TIPOS_CONTRATO.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
              </div>
            </Section>

            <Section title="Dados pessoais">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <F label="Nome completo" req><Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="funcionario-form-nome" /></F>
                <F label="Data de nascimento"><Input type="date" value={form.data_nasc || ""} onChange={(e) => setForm({ ...form, data_nasc: e.target.value })} /></F>
                <F label="CPF"><Input placeholder="000.000.000-00" value={form.cpf || ""} onChange={(e) => setForm({ ...form, cpf: maskCPF(e.target.value) })} /></F>
                <F label="RG"><Input placeholder="00.000.000-0" value={form.rg || ""} onChange={(e) => setForm({ ...form, rg: maskRG(e.target.value) })} /></F>
                <F label="Telefone"><Input placeholder="(00) 00000-0000" value={form.telefone || ""} onChange={(e) => setForm({ ...form, telefone: maskPhone(e.target.value) })} /></F>
                <F label="E-mail"><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="funcionario-form-email" /></F>
              </div>
            </Section>

            <Section title="Endereço">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                <div className="md:col-span-2"><F label="CEP"><Input placeholder="00000-000" value={form.endereco?.cep || ""} onChange={async (e) => {
                  const masked = maskCEP(e.target.value);
                  setForm((f) => ({ ...f, endereco: { ...f.endereco, cep: masked } }));
                  if (onlyDigits(masked).length === 8) {
                    const r = await fetchCEP(masked);
                    if (r) setForm((f) => ({ ...f, endereco: { ...f.endereco, logradouro: r.logradouro || f.endereco?.logradouro || "", bairro: r.bairro || f.endereco?.bairro || "", cidade: r.cidade || f.endereco?.cidade || "", estado: r.estado || f.endereco?.estado || "" } }));
                  }
                }} /></F></div>
                <div className="md:col-span-4"><F label="Logradouro"><Input value={form.endereco?.logradouro || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, logradouro: e.target.value } })} /></F></div>
                <div><F label="Nº"><Input value={form.endereco?.numero || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, numero: e.target.value } })} /></F></div>
                <div className="md:col-span-2"><F label="Bairro"><Input value={form.endereco?.bairro || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, bairro: e.target.value } })} /></F></div>
                <div className="md:col-span-2"><F label="Cidade"><Input value={form.endereco?.cidade || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, cidade: e.target.value } })} /></F></div>
                <div><F label="UF"><Input maxLength={2} value={form.endereco?.estado || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, estado: e.target.value.toUpperCase() } })} /></F></div>
              </div>
            </Section>

            <Section title="Atuação profissional">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <F label="Registro do conselho"><Input value={form.registro_conselho || ""} placeholder="Ex: CREFITO 12345 / CRP 06/12345" onChange={(e) => setForm({ ...form, registro_conselho: e.target.value })} /></F>
                <F label="Formação"><Input value={form.formacao || ""} onChange={(e) => setForm({ ...form, formacao: e.target.value })} /></F>
                <F label="Data de admissão"><Input type="date" value={form.data_admissao || ""} onChange={(e) => setForm({ ...form, data_admissao: e.target.value })} /></F>
                <F label="Disponibilidade" className="md:col-span-2"><Textarea rows={2} placeholder="Segunda a sexta 8h-17h" value={form.disponibilidade || ""} onChange={(e) => setForm({ ...form, disponibilidade: e.target.value })} /></F>
              </div>
            </Section>

            {canSeeFinanceiro && (
              <Section title="Financeiro">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <F label="Valor por hora (R$)"><Input type="number" step="0.01" value={form.valor_hora} onChange={(e) => setForm({ ...form, valor_hora: e.target.value })} placeholder="0.00" /></F>
                  <F label="Salário mensal único (R$) — opcional, use se não configurar múltiplos abaixo"><Input type="number" step="0.01" value={form.salario_mensal} onChange={(e) => setForm({ ...form, salario_mensal: e.target.value })} placeholder="0.00" /></F>
                </div>

                <div className="mt-4 rounded-lg border border-brand-turquoise/30 bg-brand-turquoise/5 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-700">Múltiplos pagamentos no mês</p>
                      <p className="text-xs text-muted-foreground">Ex: parcela no dia 15 e outra no dia 30. Se preenchido, ignora o salário mensal único.</p>
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, pagamentos: [...(form.pagamentos || []), { _k: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `pg-${Date.now()}-${Math.random()}`, dia: "", valor: "", descricao: "" }] })} data-testid="pagamento-add">
                      + Adicionar parcela
                    </Button>
                  </div>
                  {(form.pagamentos || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-2">Nenhuma parcela — usará o salário mensal único acima.</p>
                  ) : (
                    <div className="space-y-2">
                      {(form.pagamentos || []).map((p, idx) => (
                        <div key={p._k || idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`pagamento-row-${idx}`}>
                          <div className="col-span-2">
                            <Label className="text-[10px] uppercase text-muted-foreground">Dia (1-31)</Label>
                            <Input type="number" min="1" max="31" value={p.dia} onChange={(e) => {
                              const cp = [...form.pagamentos]; cp[idx] = { ...cp[idx], dia: e.target.value }; setForm({ ...form, pagamentos: cp });
                            }} data-testid={`pagamento-dia-${idx}`} />
                          </div>
                          <div className="col-span-3">
                            <Label className="text-[10px] uppercase text-muted-foreground">Valor (R$)</Label>
                            <Input type="number" step="0.01" value={p.valor} onChange={(e) => {
                              const cp = [...form.pagamentos]; cp[idx] = { ...cp[idx], valor: e.target.value }; setForm({ ...form, pagamentos: cp });
                            }} data-testid={`pagamento-valor-${idx}`} />
                          </div>
                          <div className="col-span-6">
                            <Label className="text-[10px] uppercase text-muted-foreground">Descrição (opcional)</Label>
                            <Input value={p.descricao || ""} placeholder="Ex: Adiantamento, Salário base" onChange={(e) => {
                              const cp = [...form.pagamentos]; cp[idx] = { ...cp[idx], descricao: e.target.value }; setForm({ ...form, pagamentos: cp });
                            }} data-testid={`pagamento-descricao-${idx}`} />
                          </div>
                          <div className="col-span-1">
                            <Button type="button" size="icon" variant="ghost" onClick={() => setForm({ ...form, pagamentos: form.pagamentos.filter((_, i) => i !== idx) })} data-testid={`pagamento-remove-${idx}`} className="text-rose-600 hover:bg-rose-50">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-4 rounded-lg border border-border bg-slate-50/60 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Dados bancários</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <F label="Tipo de PIX">
                      <Select value={form.dados_bancarios?.pix_tipo || ""} onValueChange={(v) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, pix_tipo: v } })}>
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cpf">CPF</SelectItem>
                          <SelectItem value="email">E-mail</SelectItem>
                          <SelectItem value="telefone">Telefone</SelectItem>
                          <SelectItem value="aleatoria">Aleatória</SelectItem>
                        </SelectContent>
                      </Select>
                    </F>
                    <F label="Chave PIX" className="md:col-span-2"><Input value={form.dados_bancarios?.pix_chave || ""} onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, pix_chave: e.target.value } })} /></F>
                    <F label="Banco"><Input value={form.dados_bancarios?.banco || ""} onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, banco: e.target.value } })} /></F>
                    <F label="Agência"><Input value={form.dados_bancarios?.agencia || ""} onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, agencia: e.target.value } })} /></F>
                    <F label="Conta"><Input value={form.dados_bancarios?.conta || ""} onChange={(e) => setForm({ ...form, dados_bancarios: { ...form.dados_bancarios, conta: e.target.value } })} /></F>
                  </div>
                </div>
              </Section>
            )}

            {!editing && CARGOS_COM_LOGIN.includes(form.cargo) && (
              <Section title="Acesso ao sistema">
                <div className="rounded-lg border-2 border-dashed border-brand-turquoise/40 bg-[#F0FBFC] p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox checked={form.criar_login} onCheckedChange={(v) => setForm({ ...form, criar_login: !!v })} data-testid="funcionario-form-criar-login" className="mt-1" />
                    <div className="flex-1">
                      <p className="font-medium text-sm text-brand-turquoise">Criar login no sistema</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        O funcionário poderá acessar o painel com e-mail e senha. No primeiro login, será obrigado a completar o próprio cadastro.
                      </p>
                      {form.criar_login && (
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <F label="Senha temporária (deixe vazio para gerar)"><Input value={form.senha_temporaria} onChange={(e) => setForm({ ...form, senha_temporaria: e.target.value })} placeholder="Auto-gerada" /></F>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Section>
            )}

            <F label="Observações"><Textarea rows={2} value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></F>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Fechar</Button>
              <Button type="submit" disabled={saving} data-testid="funcionario-form-submit" className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white">{saving ? "Salvando…" : (editing ? "Salvar" : "Cadastrar")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir funcionário?</AlertDialogTitle><AlertDialogDescription>O funcionário será movido para a Lixeira. Se tiver login, será desativado. É possível restaurar depois.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Credenciais (nova senha temporária) */}
      <Dialog open={credentialsOpen} onOpenChange={(o) => { setCredentialsOpen(o); if (!o) setCredentials(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-brand-gold" /> Credenciais geradas</DialogTitle>
            <DialogDescription>Copie e envie ao funcionário. Estas informações não serão exibidas novamente.</DialogDescription>
          </DialogHeader>
          {credentials && (
            <div className="rounded-xl border-2 border-brand-gold bg-[#FFF9E6] p-5 space-y-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[#8B6914] mb-1">E-mail de acesso</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded-md border font-mono text-sm break-all">{credentials.email}</code>
                  <Button type="button" size="icon" variant="outline" onClick={() => { navigator.clipboard.writeText(credentials.email); toast.success("E-mail copiado"); }} data-testid="reset-copy-email"><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[#8B6914] mb-1">Senha temporária</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded-md border font-mono text-lg tracking-wide">{credentials.senha_temporaria}</code>
                  <Button type="button" size="icon" variant="outline" onClick={() => { navigator.clipboard.writeText(credentials.senha_temporaria); toast.success("Senha copiada"); }} data-testid="reset-copy-senha"><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
              <p className="text-xs text-[#8B6914] leading-relaxed">
                No primeiro acesso, o funcionário será solicitado a completar o cadastro e definir uma nova senha permanente.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button type="button" onClick={() => {
              if (credentials) navigator.clipboard.writeText(`E-mail: ${credentials.email}\nSenha: ${credentials.senha_temporaria}`);
              toast.success("Credenciais copiadas");
            }} className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white" data-testid="reset-copy-all">
              <Copy className="h-4 w-4 mr-1" /> Copiar tudo
            </Button>
            <Button type="button" variant="outline" onClick={() => setCredentialsOpen(false)} data-testid="reset-close">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TONE = {
  turquoise: "bg-[#DFF4F5] text-[#2A7A80]",
  lilac: "bg-[#EDE1F3] text-[#7B569A]",
  pink: "bg-[#FBE1EC] text-[#C4547E]",
  gold: "bg-[#FFF3D6] text-[#8B6914]",
};

function StatBox({ label, value, tone, icon: Icon }) {
  return (
    <div className={`rounded-xl p-4 ${TONE[tone]}`} data-testid={`funcionarios-stat-${label}`}>
      <div className="flex items-center gap-2 mb-1 opacity-80">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        <p className="text-[10px] uppercase tracking-wider font-semibold">{label}</p>
      </div>
      <p className="font-heading text-2xl font-bold">{value}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-brand-turquoise mb-3">{title}</h3>
      {children}
    </section>
  );
}

function F({ label, req, className, children }) {
  return (<div className={`space-y-1.5 ${className || ""}`}><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}{req && <span className="text-destructive"> *</span>}</Label>{children}</div>);
}
